import { t } from 'i18next';
import { FC, useMemo, useState } from 'react';
import { usePushNotification } from '../../scripts/hooks/usePushNotification';
import { useFeature } from '../../scripts/selectors/global';
import { FeatureFlag } from '../../../gameConfig/sharedTypes';
import { Link } from '../link';
import { TPushTopic } from '../../../common/pushNotification';

type TTopicNode = {
  topic: TPushTopic;
  labelKey: string;
  descriptionKey: string;
  children?: TTopicNode[];
};

const TOPIC_TREE: TTopicNode[] = [
  {
    topic: 'coverage.rwf.post',
    labelKey: 'settings:push-notifications.topics.rwf-posts',
    descriptionKey: 'settings:push-notifications.topics.rwf-posts-description',
    children: [
      {
        topic: 'coverage.rwf.post.breaking-news',
        labelKey: 'settings:push-notifications.topics.rwf-posts-breaking-news',
        descriptionKey: 'settings:push-notifications.topics.rwf-posts-breaking-news-description'
      },
      {
        topic: 'coverage.rwf.post.analysis',
        labelKey: 'settings:push-notifications.topics.rwf-posts-analysis',
        descriptionKey: 'settings:push-notifications.topics.rwf-posts-analysis-description'
      },
      {
        topic: 'coverage.rwf.post.social',
        labelKey: 'settings:push-notifications.topics.rwf-posts-social',
        descriptionKey: 'settings:push-notifications.topics.rwf-posts-social-description'
      },
      {
        topic: 'coverage.rwf.post.recap',
        labelKey: 'settings:push-notifications.topics.rwf-posts-recap',
        descriptionKey: 'settings:push-notifications.topics.rwf-posts-recap-description'
      }
    ]
  },
  {
    topic: 'coverage.rwf.post.guild',
    labelKey: 'settings:push-notifications.topics.rwf-guild',
    descriptionKey: 'settings:push-notifications.topics.rwf-guild-description',
    children: [
      {
        topic: 'coverage.rwf.post.guild.liquid',
        labelKey: 'settings:push-notifications.topics.rwf-guild-liquid',
        descriptionKey: 'settings:push-notifications.topics.rwf-guild-liquid-description'
      },
      {
        topic: 'coverage.rwf.post.guild.method',
        labelKey: 'settings:push-notifications.topics.rwf-guild-method',
        descriptionKey: 'settings:push-notifications.topics.rwf-guild-method-description'
      },
      {
        topic: 'coverage.rwf.post.guild.echo',
        labelKey: 'settings:push-notifications.topics.rwf-guild-echo',
        descriptionKey: 'settings:push-notifications.topics.rwf-guild-echo-description'
      },
      {
        topic: 'coverage.rwf.post.guild.china',
        labelKey: 'settings:push-notifications.topics.rwf-guild-china',
        descriptionKey: 'settings:push-notifications.topics.rwf-guild-china-description'
      }
    ]
  }
];

type TNotificationControlPanelProps = {
  onClose?: () => void;
  showSettingsLink?: boolean;
  allowDirectEnable?: boolean;
  compact?: boolean;
};

function getParentTopics(topic: string): string[] {
  const parts = topic.split('.');
  const parents: string[] = [];

  for (let i = parts.length - 1; i > 0; i -= 1) {
    parents.push(parts.slice(0, i).join('.'));
  }

  return parents;
}

export const NotificationControlPanel: FC<TNotificationControlPanelProps> = ({
  onClose,
  showSettingsLink,
  allowDirectEnable = false,
  compact = false
}) => {
  const {
    permissionState,
    subscription,
    topics,
    subscribe,
    unsubscribe,
    subscribeToTopic,
    unsubscribeFromTopic,
    isLoading
  } = usePushNotification();
  const isAuthenticated = useFeature(FeatureFlag.AUTHENTICATED);
  const [expandedTopics, setExpandedTopics] = useState<Record<string, boolean>>({
    'coverage.rwf': true
  });

  const isSubscribed = !!subscription;
  const isDenied = permissionState === 'denied';
  const settingsUrl = isAuthenticated
    ? '/settings/notifications'
    : `/login?next=${encodeURIComponent('/settings/notifications')}`;

  const topicLookup = useMemo(() => {
    return new Set(topics.map((topic) => topic.topic));
  }, [topics]);

  const topicLabelLookup = useMemo(() => {
    const labels = new Map<string, string>();
    const visit = (nodes: TTopicNode[]) => {
      nodes.forEach((node) => {
        labels.set(node.topic, t(node.labelKey));
        if (node.children) {
          visit(node.children);
        }
      });
    };
    visit(TOPIC_TREE);
    return labels;
  }, []);

  const getInheritedFrom = (topic: string): string | null => {
    const parents = getParentTopics(topic);
    return parents.find((parentTopic) => topicLookup.has(parentTopic)) || null;
  };

  const isDirectlySubscribed = (topic: string) => topicLookup.has(topic);
  const isEffectivelySubscribed = (topic: string) => isDirectlySubscribed(topic) || !!getInheritedFrom(topic);
  const displayedTopicTree = TOPIC_TREE;

  const handleTopicChange = (topic: string, checked: boolean) => {
    if (checked) {
      subscribeToTopic(topic);
      return;
    }

    unsubscribeFromTopic(topic);
  };

  const toggleExpanded = (topic: string) => {
    setExpandedTopics((prev) => ({
      ...prev,
      [topic]: !prev[topic]
    }));
  };

  const renderTopicNode = (node: TTopicNode, depth = 0) => {
    const hasChildren = !!node.children?.length;
    const inheritedFrom = getInheritedFrom(node.topic);
    const checked = isEffectivelySubscribed(node.topic);
    const disabled = isLoading || !!inheritedFrom;
    const defaultExpanded = depth === 0 ? true : !checked;
    const isExpanded = expandedTopics[node.topic] ?? defaultExpanded;

    return (
      <div
        key={node.topic}
        className={`rio-notification-panel__topic-group${compact ? ' rio-notification-panel__topic-group--compact' : ''}`}
      >
        <div className="rio-notification-panel__topic" style={{ paddingLeft: `${depth * 20}px` }}>
          <div className="rio-notification-panel__topic-main">
            {hasChildren ? (
              <button
                type="button"
                className="rio-notification-panel__expand"
                onClick={() => toggleExpanded(node.topic)}
                aria-label={isExpanded ? 'Collapse topic' : 'Expand topic'}
              >
                <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'}`} />
              </button>
            ) : (
              <span className="rio-notification-panel__expand-placeholder" />
            )}

            <label className="rio-notification-panel__checkbox-row">
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(event) => handleTopicChange(node.topic, event.target.checked)}
              />
              <span className="rio-notification-panel__topic-info">
                <span className="rio-notification-panel__topic-label">{t(node.labelKey)}</span>
                {!compact && <span className="rio-notification-panel__topic-desc">{t(node.descriptionKey)}</span>}
                {!compact && inheritedFrom && (
                  <span className="rio-notification-panel__topic-meta">
                    {t('settings:push-notifications.included-via', {
                      topic: topicLabelLookup.get(inheritedFrom) || inheritedFrom
                    })}
                  </span>
                )}
              </span>
            </label>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="rio-notification-panel__topic-children">
            {node.children!.map((child) => renderTopicNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`rio-notification-panel${compact ? ' rio-notification-panel--compact' : ''}`}>
      <div className="rio-notification-panel__status">
        {isDenied && (
          <>
            <p className="slds-text-body--small text-warning slds-m-bottom--x-small">
              <i className="fa fa-exclamation-triangle slds-m-right--x-small" />
              {t('nav:notifications.blocked-message')}
            </p>
            <p className="slds-text-body--small text-muted">{t('nav:notifications.check-settings')}</p>
          </>
        )}

        {!isDenied && !isSubscribed && (
          <>
            <p className="slds-text-body--small text-muted slds-m-bottom--small">
              {t(allowDirectEnable ? 'settings:push-notifications.pre-permission' : 'nav:notifications.not-enabled')}
            </p>
            {allowDirectEnable ? (
              <button
                className="slds-button slds-button--brand slds-button--small"
                onClick={async () => {
                  await subscribe();
                }}
                disabled={isLoading}
              >
                <i className="fa fa-bell slds-m-right--x-small" />
                {t('nav:notifications.enable')}
              </button>
            ) : (
              <Link
                to={settingsUrl}
                className="slds-button slds-button--neutral slds-button--small"
                onClick={onClose}
              >
                <i className="fa fa-sliders slds-m-right--x-small" />
                {t('nav:notifications.setup')}
              </Link>
            )}
          </>
        )}

        {!isDenied && isSubscribed && (
          <>
            <div className="slds-m-bottom--small">
              <span className="text-success slds-text-body--small">
                <i className="fa fa-check-circle slds-m-right--x-small" />
                {t('settings:push-notifications.enabled-status')}
              </span>
            </div>
            <button
              className="slds-button slds-button--neutral slds-button--small"
              onClick={unsubscribe}
              disabled={isLoading}
            >
              <i className="fa fa-bell-slash slds-m-right--x-small" />
              {t('settings:push-notifications.disable-button')}
            </button>
          </>
        )}
      </div>

      {isSubscribed && (
        <div className="rio-notification-panel__topics">
          <div className="rio-notification-panel__topics-header">
            <div className={compact ? 'slds-text-heading--xx-small text-bold' : 'slds-text-heading--x-small text-bold'}>
              {t('settings:push-notifications.topics-heading')}
            </div>
            {!compact && (
              <div className="slds-text-body--small text-muted slds-m-top--xx-small">
                {t('settings:push-notifications.topics-subheading')}
              </div>
            )}
          </div>

          <div className="rio-notification-panel__tree">{displayedTopicTree.map((node) => renderTopicNode(node))}</div>
        </div>
      )}

      {showSettingsLink && (
        <div className="rio-notification-panel__footer">
          <Link to={settingsUrl} className="slds-text-body--small text-brand" onClick={onClose}>
            {t('nav:notifications.manage-settings')}
          </Link>
        </div>
      )}
    </div>
  );
};
